// =====================================================================
// src/modulos/visitas.js — fornecedores e visitas (0045/0046)
//
// O QUE O HUGO DECIDIU (20/09/2026), e que esta tela obedece:
//
//   "fornecedores apenas administração do hotel - pode-se cadastrar placas
//    para visitas futuras"
//   "prestadores de serviço externo o proprietário ou administrador de
//    flats pode cadastrar"
//   "o prestador externo não tem acesso ao sistema - sua visita é agendada
//    pelo proprietário, administrador ou pelo próprio hotel - ele deve se
//    dirigir à recepção - para cadastro e liberação de entrada"
//   "O proprietário não enxerga as visitas dos outros apartamentos"
//
// 0100/0101 (Hugo, 02/11/2026, 2A): o proprietário não tem login; quem
// agenda e vê as visitas dos flats é a IMOBILIÁRIA, com `ver visitas`, na
// visão 'dono' (lib/visitas.js). Ela agenda só nos flats dela e só com
// fornecedor dela (0100), e só vê os dela (0101).
//
// DE ONDE VEIO O DESENHO
//
// O Monteiro não tem tela de fornecedor nem de visita — o prédio dele é
// de flats sem guarita. Então esta tela é montada com os componentes de
// lá que este app já copiou: o `.gov-cabeca` com `.usu-filtros` e o botão
// à direita (`renderUsuarios`, 17047), a `table.table-cards` com
// `data-label` (`renderGaragem`, 8853), as abas de `barraDeAbas`, o modal
// com `.modal-title` / `.label` / `.input` / `.acoes`, e a busca de
// repetidos do modal de Hóspedes (`hospedes.js`, que veio de
// `renderHospedes`, 15073).
//
// QUEM VÊ O QUÊ NÃO ESTÁ AQUI. Está no RLS e nas funções da 0045/0046.
// `visaoDe()` escolhe o DESENHO; se ela errasse, o banco continuaria
// recusando.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, confirmar, enquantoAtualiza, barraDeAbas } from '../ui.js';
import { hojeDe } from '../lib/contexto.js';
import { normalizarPlaca } from '../lib/garagem.js';
import { tipoSugerido } from '../lib/hospedes.js';
import { visaoDe, podeCadastrarFornecedor, podeEditarFornecedor, rotuloDeQuem, situacaoDaVisita, acoesDaVisita,
         textoDaJanela, validarAgendamento, validarChegada, argumentosDoAgendamento,
         preencherDeVisitante, rotuloVisitante, filtrarFornecedores } from '../lib/visitas.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const visao = visaoDe(contexto);
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  // O Administrador do Sistema não tem cliente (`contexto.cliente` é NULO
  // de propósito, CLAUDE.md §3.2) e escolhe o hotel num seletor — o mesmo
  // padrão de checkin.js/reservas.js. Ler `contexto.cliente.id` aqui
  // quebrava a tela para o Hugo: "Cannot read properties of null" (20/09).
  let clienteSel = sistema ? null : contexto.cliente.id;
  let clientes = [];
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});
  const hoje = () => hojeDe(contexto, agora());
  const podeCadastrar = podeCadastrarFornecedor(contexto);
  const dono = visao === 'dono';
  let vivo = true;
  let aba = 'visitas';
  let dia = hoje();
  let visitas = [], fornecedores = [], unidades = [];
  const filtro = { texto: '', comInativos: false };

  function refazer() { return enquantoAtualiza(async () => { await carregar(); desenhar(); }); }

  async function carregar() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (sistema && !clienteSel) { visitas = []; fornecedores = []; unidades = []; return; }
    const pedidos = [
      auth.rpc('visitas_do_dia', { p_data: dia, ...pc() }),
      auth.rpc('fornecedores_do_cliente', { ...pc() }),
    ];
    // O quarto, para agendar. A imobiliária só vê os flats dela no seletor —
    // e o banco confere de novo (`agendar_visita`, 0100: "Este flat não é
    // da sua imobiliária"). A portaria não agenda; não precisa da lista. O
    // sistema vê todos os hotéis pela RLS, então o corte pelo cliente vai
    // na consulta.
    if (visao !== 'portaria') {
      const corte = dono && contexto.imobiliaria && contexto.imobiliaria.id ? `&imobiliaria_id=eq.${contexto.imobiliaria.id}`
                  : sistema ? `&cliente_id=eq.${clienteSel}` : '';
      pedidos.push(auth.ler('unidades', `select=id,numero,andar&ativo=eq.true${corte}&order=numero`));
    }
    const r = await Promise.all(pedidos);
    visitas = r[0] || []; fornecedores = r[1] || []; unidades = r[2] || [];
  }

  const seletorDeCliente = () => (sistema
    ? el('select', { class: 'input', id: 'vi-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; refazer(); } },
        clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome)))
    : null);

  function erroModal(msg) { const e = document.getElementById('modal-erro'); if (e) { e.textContent = msg || ''; e.classList.toggle('hidden', !msg); } }
  const ativos = () => fornecedores.filter(f => f.ativo !== false);

  // ── AS VISITAS DO DIA ──────────────────────────────────────────────
  function tabelaDeVisitas() {
    const comAcoes = visao !== 'portaria';
    const corpo = el('tbody', {}, visitas.length ? visitas.map(v => {
      const s = situacaoDaVisita(v);
      const acoes = acoesDaVisita(v, visao, hoje());
      return el('tr', { dataset: { visita: v.id, situacao: s.chave } },
        el('td', { 'data-label': 'Horário' }, textoDaJanela(v.janela_de, v.janela_ate)),
        el('td', { 'data-label': 'Fornecedor', style: { fontWeight: '700' } }, v.fornecedor,
          v.ramo ? el('div', { class: 'usu-email' }, v.ramo) : null),
        el('td', { 'data-label': 'Destino' }, v.unidade || 'Prédio'),
        el('td', { 'data-label': 'Motivo' }, v.motivo || '—'),
        el('td', { 'data-label': 'Placa', style: { letterSpacing: '1px' } }, v.placa || el('span', { style: { color: '#9ca3af' } }, 'sem carro')),
        el('td', { 'data-label': 'Quem veio' }, v.pessoa_nome
          ? el('span', {}, v.pessoa_nome, v.pessoa_documento ? el('div', { class: 'usu-email' }, v.pessoa_documento) : null)
          : el('span', { style: { color: '#9ca3af' } }, 'na chegada')),
        el('td', { 'data-label': 'Situação' }, el('span', { class: 'estado ' + s.classe }, s.rotulo)),
        comAcoes ? el('td', { 'data-label': '' }, el('div', { class: 'usu-acoes' },
          acoes.includes('chegada') ? el('button', { type: 'button', class: 'btn btn-verde btn-sm', dataset: { chegada: v.id }, onclick: () => modalChegada(v) }, 'Chegou') : null,
          acoes.includes('remarcar') ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { remarcar: v.id }, onclick: () => modalRemarcar(v) }, 'Remarcar') : null,
          acoes.includes('saida') ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { saida: v.id }, onclick: () => saida(v) }, 'Saiu') : null,
          acoes.includes('cancelar') ? el('button', { type: 'button', class: 'btn btn-contorno-vermelho btn-sm', dataset: { cancelar: v.id }, onclick: () => modalCancelar(v) }, 'Cancelar') : null)) : null);
    }) : el('tr', {}, el('td', { colspan: comAcoes ? 8 : 7, style: { textAlign: 'center', color: '#9ca3af', padding: '20px' } },
        dono ? 'Nenhuma visita agendada para os seus flats neste dia.' : 'Nenhuma visita neste dia.')));

    return el('div', { class: 'card', style: { overflowX: 'auto' } },
      el('h3', { class: 'secao-titulo' }, `Visitas (${visitas.length})`),
      el('table', { class: 'table-cards', id: 'tabela-visitas' },
        el('thead', {}, el('tr', {},
          el('th', {}, 'Horário'), el('th', {}, 'Fornecedor'), el('th', {}, 'Destino'), el('th', {}, 'Motivo'),
          el('th', {}, 'Placa'), el('th', {}, 'Quem veio'), el('th', {}, 'Situação'), comAcoes ? el('th', {}) : null)),
        corpo));
  }

  // ── AGENDAR ────────────────────────────────────────────────────────
  function modalAgendar() {
    // 0100: a imobiliária agenda só com fornecedor DELA
    const lista = dono ? ativos().filter(f => f.de_quem === 'imobiliaria') : ativos();
    if (!lista.length) { aviso(dono ? 'Cadastre um fornecedor antes de agendar.' : 'Nenhum fornecedor ativo. Cadastre um antes de agendar.', 'erro'); return; }
    const c = {};
    const campo = (id, rotulo, input) => { c[id] = input; return el('div', {}, el('label', { class: 'label', for: id }, rotulo), input); };
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, 'Agendar visita'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        'A guarita libera pela placa no dia e dentro do horário. Fora dele, ou sem placa, o fornecedor se apresenta na recepção.'),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      campo('ag-fornecedor', 'Fornecedor', el('select', { class: 'input', id: 'ag-fornecedor' },
        el('option', { value: '' }, '— escolha —'),
        lista.map(f => el('option', { value: f.id }, f.nome + (f.ramo ? ' · ' + f.ramo : ''))))),
      el('div', { class: 'form-row' },
        campo('ag-data', 'Data', el('input', { class: 'input', id: 'ag-data', type: 'date', value: dia, min: hoje() })),
        campo('ag-unidade', dono ? 'Quarto' : 'Destino', el('select', { class: 'input', id: 'ag-unidade' },
          dono ? null : el('option', { value: '' }, 'Prédio (áreas comuns)'),
          dono && unidades.length !== 1 ? el('option', { value: '' }, '— escolha —') : null,
          unidades.map(u => el('option', { value: u.id }, u.numero))))),
      el('div', { class: 'form-row' },
        campo('ag-de', 'Chega a partir de', el('input', { class: 'input', id: 'ag-de', type: 'time' })),
        campo('ag-ate', 'Até', el('input', { class: 'input', id: 'ag-ate', type: 'time' }))),
      el('div', { class: 'form-row' },
        campo('ag-placa', 'Placa do carro (opcional)', el('input', { class: 'input', id: 'ag-placa', placeholder: 'ABC1D23', maxlength: '8',
          style: { letterSpacing: '2px', fontWeight: '700' }, oninput: ev => { ev.target.value = normalizarPlaca(ev.target.value); } })),
        campo('ag-motivo', 'Motivo', el('input', { class: 'input', id: 'ag-motivo', placeholder: 'troca do chuveiro' }))),
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Cancelar'),
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-agendar',
          onclick: async () => {
            const f = { fornecedor: c['ag-fornecedor'].value, data: c['ag-data'].value, unidade: c['ag-unidade'].value,
                        janela_de: c['ag-de'].value, janela_ate: c['ag-ate'].value, placa: c['ag-placa'].value, motivo: c['ag-motivo'].value };
            const erros = validarAgendamento(f, { hoje: hoje(), dono });
            if (erros.length) { erroModal(erros.join(' · ')); return; }
            try { await auth.rpc('agendar_visita', { ...argumentosDoAgendamento(f), ...pc() }); }
            catch (e) { erroModal(e.message); return; }
            fecharModal();
            if (f.data !== dia) { dia = f.data; }
            await refazer(); aviso('Visita agendada.');
          } }, 'Agendar')));
    abrirModal(corpo);
  }

  // ── A CHEGADA (recepção) ───────────────────────────────────────────
  // É aqui que a PESSOA entra: nome e sobrenome, documento, e a placa que
  // o carro trouxe — "depois que a recepção atualiza a placa, o carro
  // volta para a guarita e tenta de novo" (Hugo).
  function modalChegada(v) {
    const c = {};
    let pessoa = null;
    const campo = (id, rotulo, input) => { c[id] = input; return el('div', {}, el('label', { class: 'label', for: id }, rotulo), input); };
    const sugestoes = el('div', { id: 'ch-sugestoes' });
    let timer = null;
    async function buscar() {
      const t = [c['ch-nome'].value, c['ch-sobrenome'].value].filter(x => x.trim()).join(' ').trim();
      const texto = t.length >= 3 ? t : (c['ch-doc'].value.replace(/\s/g, '').length >= 3 ? c['ch-doc'].value : '');
      sugestoes.replaceChildren();
      if (!texto || pessoa) return;
      let achados = [];
      try { achados = await auth.rpc('visitantes_repetidos', { p_texto: texto, ...pc() }); } catch (_) { return; }
      if (!achados.length) return;
      sugestoes.appendChild(el('div', { class: 'usu-grupo', style: { margin: '0 0 4px' } }, 'Já veio aqui'));
      for (const p of achados.slice(0, 5)) {
        sugestoes.appendChild(el('button', { type: 'button', class: 'btn btn-secondary btn-sm',
          style: { display: 'block', width: '100%', textAlign: 'left', marginBottom: '4px' }, dataset: { sugestao: p.id },
          onclick: () => {
            const d = preencherDeVisitante(p);
            pessoa = d.pessoa;
            c['ch-nome'].value = d.nome; c['ch-sobrenome'].value = d.sobrenome; c['ch-doc'].value = d.documento;
            c['ch-tipo'].value = d.tipo_documento; c['ch-tel'].value = d.telefone;
            sugestoes.replaceChildren();
          } }, rotuloVisitante(p)));
      }
    }
    const aoDigitar = () => { pessoa = null; clearTimeout(timer); timer = setTimeout(buscar, 250); };

    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, 'Chegada do fornecedor'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        `${v.fornecedor} · ${v.unidade || 'Prédio'} · ${textoDaJanela(v.janela_de, v.janela_ate)}`),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('div', { class: 'form-row' },
        campo('ch-nome', 'Nome', el('input', { class: 'input', id: 'ch-nome', autocomplete: 'off', oninput: aoDigitar })),
        campo('ch-sobrenome', 'Sobrenome', el('input', { class: 'input', id: 'ch-sobrenome', autocomplete: 'off', oninput: aoDigitar }))),
      sugestoes,
      el('div', { class: 'form-row' },
        campo('ch-doc', 'Documento', el('input', { class: 'input', id: 'ch-doc', autocomplete: 'off', oninput: aoDigitar })),
        campo('ch-tipo', 'Tipo', el('select', { class: 'input', id: 'ch-tipo' },
          el('option', { value: '' }, '— pelo número —'),
          // CPF, RG ou Passaporte — o CHECK da 0047. RG só aqui, no
          // visitante: o hóspede (FNRH) continua CPF ou passaporte.
          el('option', { value: 'CPF' }, 'CPF'), el('option', { value: 'RG' }, 'RG'), el('option', { value: 'PASSAPORTE' }, 'Passaporte')))),
      el('div', { class: 'form-row' },
        campo('ch-tel', 'Telefone', el('input', { class: 'input', id: 'ch-tel', type: 'tel' })),
        campo('ch-placa', 'Placa do carro', el('input', { class: 'input', id: 'ch-placa', value: v.placa || '', placeholder: 'ABC1D23', maxlength: '8',
          style: { letterSpacing: '2px', fontWeight: '700' }, oninput: ev => { ev.target.value = normalizarPlaca(ev.target.value); } }))),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginTop: '6px' } },
        'A placa de hoje vale na guarita assim que você confirmar.'),
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Voltar'),
        el('button', { type: 'button', class: 'btn btn-verde', id: 'btn-confirmar-chegada',
          onclick: async () => {
            const d = { pessoa, nome: c['ch-nome'].value, sobrenome: c['ch-sobrenome'].value };
            const erros = validarChegada(d);
            if (erros.length) { erroModal(erros.join(' · ')); return; }
            const doc = c['ch-doc'].value.trim() || null;
            try {
              await auth.rpc('chegada_da_visita', {
                p_visita: v.id, p_pessoa: pessoa,
                p_nome: pessoa ? null : d.nome.trim(), p_sobrenome: pessoa ? null : d.sobrenome.trim(),
                p_documento: doc, p_tipo_documento: doc ? (c['ch-tipo'].value || tipoSugerido(doc) || null) : null,
                p_telefone: c['ch-tel'].value.trim() || null,
                p_placa: normalizarPlaca(c['ch-placa'].value) || null, ...pc() });
            } catch (e) { erroModal(e.message); return; }
            fecharModal(); await refazer(); aviso('Entrada liberada.');
          } }, 'Liberar entrada')));
    abrirModal(corpo);
    setTimeout(() => c['ch-nome'].focus(), 0);
  }

  async function saida(v) {
    try { await auth.rpc('saida_da_visita', { p_visita: v.id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    await refazer(); aviso(`${v.fornecedor} saiu.`);
  }

  // 0091 (4A): a visita de outro dia não se libera — remarca-se. Só a data
  // muda; janela, placa e motivo continuam (`remarcar_visita`).
  function modalRemarcar(v) {
    const data = el('input', { class: 'input', id: 'remarcar-data', type: 'date', value: hoje(), min: hoje() });
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, 'Remarcar visita'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        `${v.fornecedor} · ${v.unidade || 'Prédio'} · ${textoDaJanela(v.janela_de, v.janela_ate)}. A entrada só se libera no dia da visita.`),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('label', { class: 'label', for: 'remarcar-data' }, 'Nova data'), data,
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Voltar'),
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-confirmar-remarcar',
          onclick: async () => {
            if (!data.value) { erroModal('Informe a nova data'); return; }
            if (data.value < hoje()) { erroModal('Visita no passado — confira a data'); return; }
            try { await auth.rpc('remarcar_visita', { p_visita: v.id, p_data: data.value, ...pc() }); }
            catch (e) { erroModal(e.message); return; }
            fecharModal(); dia = data.value; await refazer(); aviso('Visita remarcada.');
          } }, 'Remarcar')));
    abrirModal(corpo);
  }

  function modalCancelar(v) {
    const motivo = el('input', { class: 'input', id: 'cancelar-motivo', placeholder: 'o fornecedor remarcou' });
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, 'Cancelar visita'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        `${v.fornecedor} · ${v.unidade || 'Prédio'} · ${textoDaJanela(v.janela_de, v.janela_ate)}. A guarita passa a recusar a placa.`),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('label', { class: 'label', for: 'cancelar-motivo' }, 'Motivo'), motivo,
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Voltar'),
        el('button', { type: 'button', class: 'btn btn-contorno-vermelho', id: 'btn-confirmar-cancelar',
          onclick: async () => {
            if (!motivo.value.trim()) { erroModal('Diga o motivo — ele fica na trilha.'); return; }
            try { await auth.rpc('cancelar_visita', { p_visita: v.id, p_motivo: motivo.value.trim(), ...pc() }); }
            catch (e) { erroModal(e.message); return; }
            fecharModal(); await refazer(); aviso('Visita cancelada.');
          } }, 'Cancelar visita')));
    abrirModal(corpo);
  }

  // ── OS FORNECEDORES ────────────────────────────────────────────────
  function tabelaDeFornecedores() {
    const lista = filtrarFornecedores(fornecedores, filtro);
    const mistos = !dono;   // no balcão os três donos aparecem juntos
    const corpo = el('tbody', {}, lista.length ? lista.map(f =>
      el('tr', { dataset: { fornecedor: f.id, deQuem: f.de_quem }, class: f.ativo === false ? 'inativo' : '' },
        el('td', { 'data-label': 'Fornecedor', style: { fontWeight: '700' } }, f.nome,
          f.documento ? el('div', { class: 'usu-email' }, f.documento) : null),
        el('td', { 'data-label': 'Ramo' }, f.ramo || '—'),
        el('td', { 'data-label': 'Contato' }, f.telefone || '—', f.email ? el('div', { class: 'usu-email' }, f.email) : null),
        mistos ? el('td', { 'data-label': 'De quem' }, rotuloDeQuem(f.de_quem)) : null,
        el('td', { 'data-label': 'Situação' }, el('span', { class: 'estado ' + (f.ativo === false ? 'est-sem_movimento' : 'est-liberado') }, f.ativo === false ? 'Inativo' : 'Ativo')),
        el('td', { 'data-label': '' }, el('div', { class: 'usu-acoes' },
          podeEditarFornecedor(f, visao, podeCadastrar) ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', 'aria-label': 'Editar', title: 'Editar', dataset: { editar: f.id }, onclick: () => modalFornecedor(f) }, icone('ic-edit')) : null))))
      : el('tr', {}, el('td', { colspan: mistos ? 6 : 5, style: { textAlign: 'center', color: '#9ca3af', padding: '20px' } },
          fornecedores.length ? 'Nenhum fornecedor com esse filtro.' : 'Nenhum fornecedor cadastrado ainda.')));

    return el('div', { class: 'card', style: { overflowX: 'auto' } },
      el('h3', { class: 'secao-titulo' }, `Fornecedores (${lista.length})`),
      el('table', { class: 'table-cards', id: 'tabela-fornecedores' },
        el('thead', {}, el('tr', {},
          el('th', {}, 'Fornecedor'), el('th', {}, 'Ramo'), el('th', {}, 'Contato'),
          mistos ? el('th', {}, 'De quem') : null, el('th', {}, 'Situação'), el('th', {}))),
        corpo));
  }


  function modalFornecedor(f) {
    const novo = !f;
    const c = {};
    const campo = (id, rotulo, input) => { c[id] = input; return el('div', {}, el('label', { class: 'label', for: id }, rotulo), input); };
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, novo ? 'Cadastrar fornecedor' : 'Editar fornecedor'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        dono ? 'Fornecedor da imobiliária: só ela e a recepção veem as visitas dele.' : 'Fornecedor do hotel: dedetização, elevador, manutenção externa.'),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('div', { class: 'form-row' },
        campo('fo-nome', 'Nome', el('input', { class: 'input', id: 'fo-nome', value: f ? f.nome : '', placeholder: 'Dedetizadora Alfa' })),
        campo('fo-ramo', 'Ramo', el('input', { class: 'input', id: 'fo-ramo', value: (f && f.ramo) || '', placeholder: 'dedetização' }))),
      el('div', { class: 'form-row' },
        campo('fo-doc', 'CNPJ / CPF', el('input', { class: 'input', id: 'fo-doc', value: (f && f.documento) || '' })),
        campo('fo-tel', 'Telefone', el('input', { class: 'input', id: 'fo-tel', type: 'tel', value: (f && f.telefone) || '' }))),
      campo('fo-email', 'E-mail', el('input', { class: 'input', id: 'fo-email', type: 'email', value: (f && f.email) || '' })),
      campo('fo-obs', 'Observação', el('input', { class: 'input', id: 'fo-obs', value: (f && f.observacao) || '' })),
      novo ? null : el('label', { style: { fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' } },
        (c['fo-ativo'] = el('input', { type: 'checkbox', id: 'fo-ativo', checked: f.ativo !== false })), 'ativo — aparece para agendar'),
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Cancelar'),
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-salvar-fornecedor',
          onclick: async () => {
            const nome = c['fo-nome'].value.trim();
            if (!nome) { erroModal('Nome do fornecedor é obrigatório'); return; }
            const ou = x => (x && x.trim() ? x.trim() : null);
            try {
              await auth.rpc('salvar_fornecedor', {
                p_id: novo ? null : f.id, p_nome: nome, p_documento: ou(c['fo-doc'].value), p_ramo: ou(c['fo-ramo'].value),
                p_telefone: ou(c['fo-tel'].value), p_email: ou(c['fo-email'].value), p_observacao: ou(c['fo-obs'].value),
                p_ativo: novo ? true : c['fo-ativo'].checked, ...pc() });
            } catch (e) { erroModal(e.message); return; }
            fecharModal(); await refazer(); aviso(novo ? 'Fornecedor cadastrado.' : 'Fornecedor salvo.');
          } }, novo ? 'Cadastrar' : 'Salvar')));
    abrirModal(corpo);
    setTimeout(() => c['fo-nome'].focus(), 0);
  }

  // ── A TELA ─────────────────────────────────────────────────────────
  function desenhar() {
    if (!vivo) return;
    cont.textContent = '';
    cont.dataset.tela = 'visitas';
    cont.dataset.visao = visao;
    cont.dataset.aba = aba;

    // A portaria só vê o dia: a lista, sem abas e sem botão.
    if (visao !== 'portaria') {
      cont.appendChild(barraDeAbas('visitas-abas',
        [{ chave: 'visitas', rotulo: 'Visitas' }, { chave: 'fornecedores', rotulo: 'Fornecedores' }],
        aba, ch => { aba = ch; desenhar(); }));
    }

    if (aba === 'fornecedores' && visao !== 'portaria') {
      cont.appendChild(el('div', { class: 'gov-cabeca' },
        el('div', { class: 'usu-filtros' },
          seletorDeCliente(),
          el('input', { type: 'search', class: 'input', id: 'fo-busca', placeholder: 'Buscar por nome, ramo ou documento', value: filtro.texto,
            oninput: ev => { filtro.texto = ev.target.value; desenhar(); } }),
          el('label', { style: { fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' } },
            el('input', { type: 'checkbox', id: 'fo-inativos', checked: filtro.comInativos, onchange: ev => { filtro.comInativos = ev.target.checked; desenhar(); } }),
            'mostrar inativos')),
        podeCadastrar
          ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'btn-novo-fornecedor', onclick: () => modalFornecedor(null) }, icone('ic-plus'), 'Cadastrar fornecedor')
          : el('div', { style: { fontSize: '12px', color: '#6b7280' } }, 'Fornecedor do hotel é cadastrado pela administração.')));
      cont.appendChild(tabelaDeFornecedores());
      const b = cont.querySelector('#fo-busca'); if (b && filtro.texto) { b.focus(); b.setSelectionRange(b.value.length, b.value.length); }
      return;
    }

    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { class: 'usu-filtros' },
        seletorDeCliente(),
        el('input', { type: 'date', class: 'input', id: 'visitas-dia', value: dia,
          onchange: ev => { if (ev.target.value) { dia = ev.target.value; refazer(); } } }),
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'visitas-hoje', onclick: () => { dia = hoje(); refazer(); } }, 'Hoje'),
        el('div', { style: { fontSize: '13px', color: '#6b7280' } },
          visao === 'portaria' ? 'Placa cadastrada e visita do dia: o portão abre. Sem placa, o fornecedor vai à recepção.'
          : dono ? 'Só as visitas dos seus flats. Quem libera a entrada é a recepção.'
          : 'O fornecedor se apresenta na recepção; a chegada libera a entrada e a placa na guarita.')),
      visao !== 'portaria'
        ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'btn-agendar-visita', onclick: modalAgendar }, icone('ic-plus'), 'Agendar visita')
        : null));
    cont.appendChild(tabelaDeVisitas());
  }

  if (visao === null) {
    cont.textContent = '';
    cont.dataset.tela = 'visitas';
    cont.dataset.visao = 'nenhuma';
    cont.appendChild(el('p', { class: 'vazio' }, 'O módulo de visitas não está contratado para este hotel.'));
    return () => { vivo = false; };
  }

  carregar().then(() => desenhar()).catch(e => { if (vivo) aviso(e.message, 'erro'); });
  return () => { vivo = false; };
}
